/* eslint-disable no-console */
import { Component, OnInit, OnDestroy } from '@angular/core';
import { WorkspaceRepository } from '@database/central';
import {
	AndroidFileExporter,
	ExportJSONFileModel,
	FileExporterDataProvider,
	IosFileExporter,
	WebFileExporter
} from '@domain/json-file-exporter';
import { QueryParamsProvider } from '@domain/routing';
import { IonRouterOutlet, NavController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { ErrorCodes, PlatformName, PlatformProvider, ToastDuration, ToastService } from '@shared';
import { LoggingService } from '@shared/logging';
import { Subscription } from 'rxjs';
import { ExportStatusEnum } from './models';

@Component({
	selector: 'app-export',
	templateUrl: './export.component.html',
	styleUrls: ['./export.component.scss']
})
export class ExportPage implements OnInit, OnDestroy {
	public currentExportStatus = ExportStatusEnum.Processing;
	public exportStatus = ExportStatusEnum;
	private backButtonSubscription: Subscription;
	private workfileId: string;
	private exportSubscription: Subscription;

	public constructor(
		// todo: disable physical back button while preparing the file for export (iOS + Android)
		private navController: NavController,
		private toastService: ToastService,
		private translateService: TranslateService,
		private fileExportService: FileExporterDataProvider,
		private platform: Platform,
		private androidFileExporter: AndroidFileExporter,
		private iosFileExporter: IosFileExporter,
		private webFileExporter: WebFileExporter,
		private logger: LoggingService,
		private routerOutlet: IonRouterOutlet,
		private platformProvider: PlatformProvider,
		private queryParamsProvider: QueryParamsProvider,
		private workspaceRepository: WorkspaceRepository
	) {}

	public ngOnInit(): void {
		this.workfileId = this.queryParamsProvider.workfileId;
		this.deactivateBackButton();
		this.exportSubscription = this.fileExportService
			.export$(this.workfileId)
			.subscribe(async (fileContents: ExportJSONFileModel) => {
				const platform = this.platformProvider.getPlatform();
				console.log(platform, "cek platform");
				switch (platform) {
					case PlatformName.ANDROID:
						try {
							await this.androidFileExporter.exportFile(fileContents);
							await this.handleSuccess();
						} catch (error) {
							this.currentExportStatus = ExportStatusEnum.Error;
							this.logger.error(ErrorCodes.AndroidExportError, error);
						}
						break;
					case PlatformName.IOS: {
						try {
							await this.iosFileExporter.exportFile(fileContents);
							await this.handleSuccess();
						} catch (error) {
							this.currentExportStatus = ExportStatusEnum.Error;
							this.logger.error(ErrorCodes.IOSExportError, error);
						}
						break;
					}
					case PlatformName.WEB: {
						try {
							await this.webFileExporter.exportFile(fileContents);
							await this.handleSuccess();
						} catch (error) {
							this.currentExportStatus = ExportStatusEnum.Error;
							this.logger.error(ErrorCodes.WebExportError, error);
						}
						break;
					}

					default:
						this.logger.error(ErrorCodes.NoPlatformDetected, 'Could not find platform.');
						throw new Error(ErrorCodes.NoPlatformDetected);
				}
			});
	}

	public ionViewDidEnter() {
		this.routerOutlet.swipeGesture = false;
	}

	public async handleSuccess(): Promise<void> {
		await this.workspaceRepository.archiveWorkfile(this.workfileId);

		this.goBack();

		this.toastService.presentSuccess(
			this.translateService.instant('exportPage.messages.success'),
			ToastDuration.Fast
		);
	}

	public goBack(): void {
		this.navController.pop();
	}

	public ionViewWillLeave() {
		if (this.platform.is('android')) {
			this.backButtonSubscription.unsubscribe();
		}

		this.routerOutlet.swipeGesture = true;
	}

	public ngOnDestroy(): void {
		if (this.exportSubscription) {
			this.exportSubscription.unsubscribe();
		}
	}

	private deactivateBackButton(): void {
		if (this.platform.is('android')) {
			this.backButtonSubscription = this.platform.backButton.subscribeWithPriority(
				9999,
				/* eslint-disable @typescript-eslint/no-unused-vars */
				(_) => {}
			);
		}
	}
}
